import { memo, useState, useMemo, useRef } from 'react';
import { Play, Pause, Search, Music, Volume2, Sparkles, Loader2, Check } from 'lucide-react';
import { PRESET_EFFECTS } from '@/features/media-library/data/preset-effects';
import { setMediaDragData, clearMediaDragData } from '@/features/media-library/utils/drag-data-cache';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import { usePlaybackStore } from '@/features/preview/stores/playback-store';
import { mediaLibraryService } from '@/features/media-library/services/media-library-service';
import type { AudioItem } from '@/types/timeline';

// SFX name → preset ID lookup (fuzzy match)
const resolveSfxToPreset = (sfxName: string): typeof PRESET_EFFECTS[0] | null => {
    const key = sfxName.toLowerCase().trim();
    for (const preset of PRESET_EFFECTS) {
        const cleanName = preset.fileName
            .replace(/^\d+[\._]\s*/, '')
            .replace(/\.[^.]+$/, '')
            .toLowerCase();
        if (cleanName === key || cleanName.includes(key) || key.includes(cleanName)) {
            return preset;
        }
    }
    for (const preset of PRESET_EFFECTS) {
        const fullNoExt = preset.fileName.replace(/\.[^.]+$/, '').toLowerCase();
        if (fullNoExt.includes(key) || key.includes(fullNoExt)) {
            return preset;
        }
    }
    return null;
};

export const SfxPanel = memo(function SfxPanel() {
    const [searchQuery, setSearchQuery] = useState('');
    const [playingId, setPlayingId] = useState<string | null>(null);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const filteredEffects = useMemo(() => {
        return PRESET_EFFECTS.filter(effect =>
            effect.fileName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const handlePlay = (id: string) => {
        if (playingId === id) {
            audioRef.current?.pause();
            setPlayingId(null);
            return;
        }
        if (audioRef.current) {
            audioRef.current.pause();
        }
        const audio = new Audio(id);
        audio.play();
        audioRef.current = audio;
        setPlayingId(id);
        audio.onended = () => {
            setPlayingId(null);
        };
    };

    const handleDragStart = (e: React.DragEvent, effect: any) => {
        e.dataTransfer.effectAllowed = 'copy';
        const dragData = {
            type: 'media-item' as const,
            mediaId: effect.id,
            mediaType: 'audio' as const,
            fileName: effect.fileName,
            duration: effect.duration,
        };
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        setMediaDragData(dragData);
    };

    // 🆕 AI 추천 배치 (로컬 알고리즘 매칭 시작)
    const handleAiAssign = async () => {
        setIsAiLoading(true);
        try {
            const items = useTimelineStore.getState().items;
            const fps = useTimelineStore.getState().fps;
            const addItem = useTimelineStore.getState().addItem;

            // 씬 아이템 수집 (Video 또는 Image 아이템들을 기준)
            const sceneItems = items
                .filter(item => item.type === 'video' || item.type === 'image')
                .sort((a, b) => a.from - b.from);

            if (sceneItems.length === 0) {
                alert('타임라인에 영상이나 이미지가 없습니다.');
                setIsAiLoading(false);
                return;
            }

            const segments = sceneItems.map((scene, i) => {
                const sceneEnd = scene.from + scene.durationInFrames;
                // 해당 씬의 시간대에 포함되는 캡션들을 하나로 이어 붙임
                const overlappingTexts = items
                    .filter(item => item.type === 'text' && (item as any).text)
                    .filter(item => {
                        const textMid = item.from + item.durationInFrames / 2;
                        return textMid >= scene.from && textMid < sceneEnd;
                    })
                    .sort((a, b) => a.from - b.from)
                    .map(item => (item as any).text)
                    .join(' ');

                return {
                    id: `scene_${i + 1}`,
                    text: overlappingTexts || scene.label || `Scene ${i + 1}`,
                    from: scene.from,
                    durationInFrames: scene.durationInFrames,
                    duration_sec: Math.round(scene.durationInFrames / fps * 10) / 10,
                };
            });

            // 🚀 [사장님 픽스] 0.00001% 천재 편집자 알고리즘 (로컬 문맥 분석 + 리듬감 형성)
            const sfxMap: Record<string, string[]> = {
                // 강한 감정 / 충격 / 부정
                "26. 폭발음": ["폭발", "대분노", "끝장", "파괴", "터짐", "쾅", "미친", "죽"],
                "35. 남자비명": ["비명", "아악", "절규", "으아", "악"],
                "때리는소리": ["싸움", "분노", "때림", "타격", "갈등", "화남", "빡침", "퍽", "아야", "아파"],
                "서프라이즈": ["기적", "대박", "놀라운", "깜짝", "미친", "대박", "우와", "헉"],
                "두둥탁 효과음": ["반전", "충격", "어이없는", "설마", "거짓말", "하지만", "그런데", "진짜"],

                // 의문 / 당황
                "31. 물음표": ["궁금", "질문", "왜", "어째서", "의문", "그럴까", "뭐지"],
                "10. 띠용": ["당황", "멍청한", "어리둥절", "황당", "어라", "음", "헐"],

                // 긍정 / 핵심 / 하이라이트
                "정답소리": ["성공", "정답", "해결", "맞춤", "완벽", "좋아", "드디어", "오케이", "콜"],
                "27. 마리오동전": ["돈", "수익", "결제", "부자", "가격", "매출", "천만", "보상", "개꿀"],
                "032_뾰로롱 마법": ["마법", "변신", "아름다운", "환상", "시작", "도입", "기쁨", "설렘", "반짝", "예쁘"],

                // 부정 / 허무
                "삑~": ["오류", "실패", "틀림", "삐용", "안돼", "엑스", "아니"],
                "놉": ["놉", "거절", "절대", "노노", "단호", "아님", "하지마"],
                "허무1": ["허무", "포기", "끝남", "지침", "한숨", "에휴", "하아", "망"],
                "37. 슬픈음악": ["슬픔", "눈물", "우울", "비극", "절망", "ㅠㅠ", "안타까운", "아쉽", "흑"],

                // 긴장 / 서스펜스
                "긴장감2": ["긴장", "불안", "조심", "위기", "숨막히는", "과연", "어떻게"],
                "긴장공포": ["공포", "무서운", "오싹", "귀신", "소름", "절망", "위험"],

                // 클로징
                "클로징(수고하셨습니다^^)": ["마무리", "끝", "결론", "구독", "좋아요", "마지막", "정리", "바바이"]
            };

            const fillerSfx = ["026_띠딩2", "038_뿅", "19. 뽀옥", "4. 삐삑", "28. 띠링", "12. 뿌직"];
            const openingSfxList = ["두둥탁 효과음", "큰북두번", "순간이동", "51. 스핀", "78. 드럼"];

            const assignments: Array<{ id: string; sfx_file: string }> = [];
            let lastSfxIndex = -99; // 편집 리듬(쿨다운) 추적용

            segments.forEach((s, index) => {
                const combinedText = s.text || "";
                if (!combinedText.trim()) return;

                let assignedSfx: string | null = null;
                const isFirst = index === 0;
                const isLast = index === segments.length - 1;

                // 구두점 기반 감정 분석 (Top-tier 편집자 스타일)
                const hasQuestionMark = combinedText.includes('?') || combinedText.includes('？');
                const hasExclamation = combinedText.includes('!') || combinedText.includes('！');
                const hasEllipsis = combinedText.includes('...') || combinedText.includes('~') || combinedText.includes('..');
                const isShortText = combinedText.length < 15;

                // 1. 문장 부호 기반의 강한 감정선 표현 (가장 감각적인 편집)
                if (hasExclamation && isShortText) {
                    const punchSfx = ["두둥탁 효과음", "서프라이즈", "26. 폭발음", "때리는소리"];
                    assignedSfx = punchSfx[Math.floor(Math.random() * punchSfx.length)]!;
                } else if (hasQuestionMark) {
                    const questionSfx = ["31. 물음표", "10. 띠용"];
                    assignedSfx = questionSfx[Math.floor(Math.random() * questionSfx.length)]!;
                } else if (hasEllipsis) {
                    const ellipsisSfx = ["허무1", "37. 슬픈음악", "긴장감2"];
                    assignedSfx = ellipsisSfx[Math.floor(Math.random() * ellipsisSfx.length)]!;
                }

                // 2. 키워드 정밀 매칭 (단어가 던지는 뉘앙스 최적화, 위 구두점 로직이 있어도 특정 단어가 더 세면 덮어씀)
                let keywordMatchStr = null;
                for (const [sfxName, keywords] of Object.entries(sfxMap)) {
                    if (keywords.some(kw => combinedText.includes(kw))) {
                        keywordMatchStr = sfxName;
                        break; // 가장 먼저 걸린 강한 키워드 채택
                    }
                }
                if (keywordMatchStr) {
                    assignedSfx = keywordMatchStr;
                }

                // 3. 씬의 위치에 따른 연출 룰 (오프닝 훅)
                if (isFirst && !assignedSfx) {
                    // 첫 장면은 시청자 이탈을 막기 위해 무조건 임팩트 있는 구강 타격감
                    assignedSfx = openingSfxList[Math.floor(Math.random() * openingSfxList.length)]!;
                }

                // 4. 리듬감 조절 및 필러 레이어 (초고수 편집자의 여백 살리기 기술)
                const distanceSinceLastSfx = index - lastSfxIndex;
                const isCoolingDown = distanceSinceLastSfx <= 1;

                if (!assignedSfx && !isFirst && !isLast) {
                    if (!isCoolingDown) {
                        // 사운드가 너무 오랫동안 비었다면 (2개 씬 이상 침묵), 시선을 끌기 위해 가벼운 사운드를 무심하게 툭 던짐 (40%)
                        if (Math.random() < 0.4) {
                            assignedSfx = fillerSfx[Math.floor(Math.random() * fillerSfx.length)]!;
                        }
                    } else {
                        // 방금 전 씬에서 소리가 났다면 다음 씬은 침묵이 원칙 지만, 아주 가끔(10%) 가벼운 연속 사운드로 리듬을 쪼갬
                        if (Math.random() < 0.1) {
                            assignedSfx = "038_뿅";
                        }
                    }
                }

                // 5. 투머치 방지 룰 (Rate Limit)
                // 연속으로 3번째 사운드가 겹칠 위기라면, 강제로 침묵시켜서 영상이 너무 조잡해지는 것을 막음
                if (assignedSfx && distanceSinceLastSfx === 1 && index > 1) {
                    const prevPrevSceneHasSfx = assignments.find(a => a.id === segments[index - 2]?.id);
                    if (prevPrevSceneHasSfx) {
                        assignedSfx = null; // 아끼는 게 낫다.
                    }
                }

                if (assignedSfx) {
                    assignments.push({ id: s.id, sfx_file: assignedSfx });
                    lastSfxIndex = index;
                }
            });

            if (assignments.length === 0) {
                alert('해당 씬에 적합한 효과음을 찾지 못했습니다.');
                setIsAiLoading(false);
                return;
            }

            // SFX 트랙 찾기: 없으면 새 SFX 트랙 생성
            const tracks = useTimelineStore.getState().tracks;
            let sfxTrack = tracks.find(t => t.name?.toLowerCase().includes('sfx'));

            if (!sfxTrack) {
                const newOrder = tracks.length > 0 ? Math.max(...tracks.map(t => t.order ?? 0)) + 1 : 0;
                sfxTrack = {
                    id: crypto.randomUUID(),
                    name: 'SFX',
                    height: 48,
                    locked: false,
                    visible: true,
                    muted: false,
                    solo: false,
                    order: newOrder,
                    items: [],
                };
                const updatedTracks = [...tracks, sfxTrack].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                useTimelineStore.getState().setTracks(updatedTracks);
            } else {
                const sfxItemsToRemove = useTimelineStore.getState().items
                    .filter(item => item.trackId === sfxTrack!.id)
                    .map(item => item.id);
                if (sfxItemsToRemove.length > 0) {
                    useTimelineStore.getState().removeItems(sfxItemsToRemove);
                }
            }

            const audioItems = await Promise.all(assignments.map(async (assignment) => {
                const sceneIdx = parseInt(assignment.id.replace('scene_', '')) - 1;
                const segment = segments[sceneIdx];
                if (!segment) return null;

                const preset = resolveSfxToPreset(assignment.sfx_file);
                if (!preset) {
                    console.warn(`[SFX AI] No preset for "${assignment.sfx_file}"`);
                    return null;
                }

                const blobUrl = await mediaLibraryService.getMediaBlobUrl(preset.id);
                if (!blobUrl) {
                    console.warn(`[SFX AI] No blob URL for "${preset.fileName}"`);
                    return null;
                }

                let actualDuration = preset.duration;
                if (!actualDuration || actualDuration === 0) {
                    actualDuration = await new Promise<number>((resolve) => {
                        const audio = new Audio(blobUrl);
                        audio.addEventListener('loadedmetadata', () => resolve(audio.duration || 2));
                        audio.addEventListener('error', () => resolve(2));
                        setTimeout(() => resolve(2), 2000);
                    });
                }

                const sceneDurationSec = segment.durationInFrames / fps;
                const maxAllowedDuration = Math.min(1.5, sceneDurationSec);

                let isTrimmed = false;
                if (actualDuration > maxAllowedDuration) {
                    actualDuration = maxAllowedDuration;
                    isTrimmed = true;
                }

                const sfxDurationFrames = Math.round(actualDuration * fps);
                return {
                    id: crypto.randomUUID(),
                    type: 'audio',
                    trackId: sfxTrack!.id,
                    from: segment.from,
                    durationInFrames: sfxDurationFrames,
                    label: `SFX: ${assignment.sfx_file}`,
                    src: blobUrl,
                    mediaId: preset.id,
                    originId: crypto.randomUUID(),
                    sourceStart: 0,
                    sourceEnd: sfxDurationFrames,
                    sourceDuration: sfxDurationFrames,
                    sourceFps: fps,
                    trimStart: 0,
                    trimEnd: 0,
                    volume: 0.7,
                    ...(isTrimmed ? { audioFadeOut: 0.5 } : {}),
                };
            }));

            const validItems = audioItems.filter((item): item is any => item !== null);
            validItems.forEach(item => addItem(item));
            const inserted = validItems.length;

            alert(`✨ ${inserted}개 효과음이 타임라인에 배치되었습니다!`);
        } catch (e: any) {
            console.error('[SFX AI] Error:', e);
            alert('효과음 배치 실패: ' + (e.message || ''));
        } finally {
            setIsAiLoading(false);
        }
    };

    const handleApplySfx = async (effect: any) => {
        try {
            const { tracks, fps, addItem, setTracks } = useTimelineStore.getState();
            const currentFrame = usePlaybackStore.getState().currentFrame;

            let sfxTrack = tracks.find(t => t.name?.toLowerCase().includes('sfx'));

            if (!sfxTrack) {
                const newOrder = tracks.length > 0 ? Math.max(...tracks.map(t => t.order ?? 0)) + 1 : 0;
                sfxTrack = {
                    id: crypto.randomUUID(),
                    name: 'SFX',
                    height: 48,
                    locked: false,
                    visible: true,
                    muted: false,
                    solo: false,
                    order: newOrder,
                    items: [],
                };
                const updatedTracks = [...tracks, sfxTrack].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setTracks(updatedTracks);
            }

            const blobUrl = await mediaLibraryService.getMediaBlobUrl(effect.id);
            if (!blobUrl) return;

            let actualDuration = effect.duration;
            if (!actualDuration || actualDuration === 0) {
                actualDuration = await new Promise<number>((resolve) => {
                    const audio = new Audio(blobUrl);
                    audio.addEventListener('loadedmetadata', () => resolve(audio.duration || 2));
                    audio.addEventListener('error', () => resolve(2));
                    setTimeout(() => resolve(2), 2000);
                });
            }

            const sfxDurationFrames = Math.round(actualDuration * fps);
            const audioItem: AudioItem = {
                id: crypto.randomUUID(),
                type: 'audio',
                trackId: sfxTrack.id,
                from: currentFrame,
                durationInFrames: sfxDurationFrames,
                label: `SFX: ${effect.fileName}`,
                src: blobUrl,
                mediaId: effect.id,
                originId: crypto.randomUUID(),
                sourceStart: 0,
                sourceEnd: sfxDurationFrames,
                sourceDuration: sfxDurationFrames,
                sourceFps: fps,
                trimStart: 0,
                trimEnd: 0,
                volume: 0.7,
            };

            addItem(audioItem);
        } catch (e) {
            console.error('[SFX] Apply failed:', e);
        }
    };

    // 🆕 AI 자동 애니메이션 배치
    const handleAiAnimationAssign = async () => {
        setIsAiLoading(true);
        try {
            const items = useTimelineStore.getState().items;
            const fps = useTimelineStore.getState().fps;
            const addItem = useTimelineStore.getState().addItem;

            const sceneItems = items
                .filter(item => item.type === 'video' || item.type === 'image')
                .filter(item => !(item as any).label?.includes('animation:'))
                .sort((a, b) => a.from - b.from);

            if (sceneItems.length === 0) {
                alert('타임라인에 영상이나 이미지가 없습니다.');
                setIsAiLoading(false);
                return;
            }

            const tracks = useTimelineStore.getState().tracks;
            let animTrack = tracks.find(t => t.name?.toLowerCase().includes('animation'));

            if (!animTrack) {
                const newOrder = 99;
                animTrack = {
                    id: crypto.randomUUID(),
                    name: 'Animation',
                    height: 48,
                    locked: false,
                    visible: true,
                    muted: false,
                    solo: false,
                    order: newOrder,
                    items: [],
                };
                const updatedTracks = [...tracks, animTrack].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                useTimelineStore.getState().setTracks(updatedTracks);
            } else {
                const animTrackId = animTrack.id;
                let updatedTracks = tracks.map(t => t.id === animTrackId ? { ...t, order: 99 } : t);
                updatedTracks = updatedTracks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                useTimelineStore.getState().setTracks(updatedTracks);

                const animItemsToRemove = useTimelineStore.getState().items
                    .filter(item => item.trackId === animTrackId)
                    .map(item => item.id);
                if (animItemsToRemove.length > 0) {
                    useTimelineStore.getState().removeItems(animItemsToRemove);
                }
            }

            let inserted = 0;
            const totalScenes = sceneItems.length;

            for (let i = 0; i < totalScenes; i++) {
                const scene = sceneItems[i];
                if (Math.random() < 0.5) {
                    const animUrl = 'https://pub-7ddc45204a7d4e9c99ae5fb7f5789299.r2.dev/animation/4_focus.mp4';
                    const animItem = {
                        id: crypto.randomUUID(),
                        type: 'video',
                        trackId: animTrack?.id ?? '',
                        from: scene?.from ?? 0,
                        durationInFrames: scene?.durationInFrames ?? fps,
                        label: `animation:${animUrl.split('/').pop()}`,
                        src: animUrl,
                        originId: crypto.randomUUID(),
                        sourceStart: 0,
                        sourceEnd: scene?.durationInFrames ?? fps,
                        sourceDuration: scene?.durationInFrames ?? fps,
                        sourceFps: fps,
                        trimStart: 0,
                        trimEnd: 0,
                        volume: 0,
                        left: 0,
                        top: 0,
                        width: 1080,
                        height: 1920,
                        zIndex: 1000,
                        opacity: 1
                    } as any;
                    addItem(animItem);
                    inserted++;
                }
            }
            alert(`✨ ${inserted}개의 애니메이션이 타임라인에 무작위 배치되었습니다!`);
        } catch (e: any) {
            console.error('[Animation AI] Error:', e);
            alert('애니메이션 자동 배치 실패: ' + (e.message || ''));
        } finally {
            setIsAiLoading(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-3 border-b border-border space-y-3">
                <div className="flex gap-2">
                    <button
                        onClick={handleAiAssign}
                        disabled={isAiLoading}
                        className="flex-1 h-9 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 px-1"
                    >
                        {isAiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {isAiLoading ? '분석 중...' : '효과음 배치'}
                    </button>
                    <button
                        onClick={handleAiAnimationAssign}
                        disabled={isAiLoading}
                        className="flex-1 h-9 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 px-1"
                    >
                        {isAiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {isAiLoading ? '배치 중...' : '애니메이션 배치'}
                    </button>
                </div>

                <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search SFX..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-secondary/30 border border-border rounded-md pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                <div className="grid grid-cols-1 gap-1">
                    {filteredEffects.map((effect) => (
                        <div
                            key={effect.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, effect)}
                            onDragEnd={() => clearMediaDragData()}
                            className="group flex items-center gap-3 p-2 rounded-lg border border-transparent hover:border-primary/30 hover:bg-secondary/30 transition-all cursor-grab active:cursor-grabbing"
                        >
                            <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                <Music className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-medium text-foreground truncate">
                                    {effect.fileName.replace(/\.[^/.]+$/, "")}
                                </div>
                                <div className="text-[9px] text-muted-foreground">SFX • Preset</div>
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleApplySfx(effect);
                                    }}
                                    title="전용 트랙에 추가"
                                    className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlay(effect.id);
                                    }}
                                    className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                                >
                                    {playingId === effect.id ? (
                                        <Pause className="w-3 h-3 fill-current" />
                                    ) : (
                                        <Play className="w-3 h-3 fill-current ml-0.5" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}

                    {filteredEffects.length === 0 && (
                        <div className="py-20 flex flex-col items-center justify-center text-center px-4 text-muted-foreground/50">
                            <Volume2 className="w-10 h-10 mb-2 opacity-20" />
                            <p className="text-xs">No sound effects found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
